<script setup lang="ts">
    import { Codemirror } from 'vue-codemirror';
    import { Extension, Prec } from '@codemirror/state';
    import { sql, MSSQL, SQLDialect } from '@codemirror/lang-sql';
    import { vscodeLightInit } from '@uiw/codemirror-theme-vscode';
    import { keymap } from '@codemirror/view';
    import { tags } from '@lezer/highlight';
    import { onMounted, ref } from 'vue';
    import { useSqlEditorStore, AutocompleteSchema } from '@/stores/sqlEditorStore.ts';
    import { BIcon } from 'buefy';

    type Props = {
        enableRunShortcut?: boolean; // Whether Ctrl-Enter and F5 should be trapped to emit a 'run' event
    };
    const props = defineProps<Props>();

    type Emits = {
        run: [void];
    };
    const emits = defineEmits<Emits>();

    const sqlEditStore = useSqlEditorStore();

    const schema = ref<AutocompleteSchema | null>(null);
    const cacheMessage = ref<string>('Fetching schema from cache...');
    const loading = ref(true);

    // The codemirror type Extension is an entirely recursive interface, which TypeScript has trouble understanding
    // Using object as the type of the ref instead as a workaround
    const extensions = ref<object | null>(null);

    function onRunKeyPressed() {
        if (props.enableRunShortcut) {
            emits('run');
            return true;
        } else {
            return false; // no override
        }
    }

    onMounted(async () => {
        schema.value = await sqlEditStore.getLibrarySchema();

        const time = sqlEditStore.getLibrarySchemaTimestamp();
        if (!time) {
            cacheMessage.value = 'Failed to fetch schema';
        } else {
            cacheMessage.value = 'Library schema last updated at ' + new Date().toLocaleTimeString();
        }

        extensions.value = [
            // Add 'run' keybinds
            keymap.of([{ key: 'F5', run: onRunKeyPressed }]),
            Prec.high(keymap.of([{ key: 'Ctrl-Enter', run: onRunKeyPressed }])), // Needs higher precedence to override default
            // Add syntax highlighter and autocomplete
            sql({
                dialect: SQLDialect.define({ ...MSSQL.spec, identifierQuotes: '["' }), // Prefer [] over ""
                upperCaseKeywords: true,
                schema: schema.value
            }),
            vscodeLightInit({ styles: [{ tag: tags.special(tags.string), color: '#0070c1' }] }) // Quoted identifiers should be light blue

        ];
        loading.value = false;
    });

    async function onRefreshSchema(): Promise<void> {
        cacheMessage.value = 'Refreshing...';
        await sqlEditStore.updateLibrarySchemaCache();
        cacheMessage.value = 'Refreshed! Reload editor to use updated schema';
    }

    const model = defineModel({
        type: String,
        required: false
    });

</script>

<template>
    <codemirror
        v-if="!loading"
        v-model="model"
        :style="{ height: '300px' }"
        :autofocus="false"
        :indent-with-tab="true"
        :tab-size="2"
        :extensions="extensions as Extension" />
    <div class="is-pulled-right is-light">
        <span class="has-text-grey-light">{{ cacheMessage }}</span>
        <b-icon
            icon="refresh"
            size="is-small"
            class="pl-2 is-clickable has-text-grey-light"
            @click="onRefreshSchema" />
    </div>
</template>
